import { Injectable, OnDestroy } from '@angular/core';
import { IntuitiveIndication, IntuitiveToothForDiagram } from '@modules/rx-intuitive-teeth-orchestrator/models';
import { RxIntuitiveTeethImagesService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-teeth-images.service';
import { finalize, takeUntil, tap } from 'rxjs/operators';
import { SafeHtml } from '@angular/platform-browser';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

@Injectable()
export class RxIntuitiveSpriteService implements OnDestroy {
	private readonly serviceDestroy$ = new Subject<void>();

	/** Combined prefixed sprite SVG markup for injection next to the diagram. */
	readonly injectedSpritesContent$: Observable<Map<string, SafeHtml>>;
	private readonly injectedSpritesContentSubject: BehaviorSubject<Map<string, SafeHtml>>;

	private readonly loadingSpriteFolders = new Set<string>();
	private readonly accumulatedSpriteMarkup: Map<string, SafeHtml> = new Map<string, SafeHtml>();

	constructor(protected readonly imagesService: RxIntuitiveTeethImagesService) {
		this.injectedSpritesContentSubject = new BehaviorSubject(new Map<string, SafeHtml>());
		this.injectedSpritesContent$ = this.injectedSpritesContentSubject.asObservable();
	}

	/**
	 * Loads sprite sheets for all indication folders needed by the current jaws (plus regular).
	 * Extra indications support view-specific sprites that are not represented by the base tooth indication.
	 * Call when upper/lower jaw diagram models change (e.g. from container subscription).
	 */
	syncInjectedSpritesContent(
		upperJaw: IntuitiveToothForDiagram[],
		lowerJaw: IntuitiveToothForDiagram[],
		extraIndicationIds: IntuitiveIndication[] = []
	): void {
		const allTeeth = [...upperJaw, ...lowerJaw];
		const uniqueIndicationIds = new Set<IntuitiveIndication>(extraIndicationIds);

		for (const t of allTeeth) {
			uniqueIndicationIds.add(t.indicationId);
		}

		type SpriteFolderIndicationId = Parameters<RxIntuitiveTeethImagesService['getSpriteFolder']>[0];

		const loadSpriteIfNeeded = (indicationId: SpriteFolderIndicationId): void => {
			const folder = this.imagesService.getSpriteFolder(indicationId);

			if (this.accumulatedSpriteMarkup.has(folder) || this.loadingSpriteFolders.has(folder)) {
				return;
			}

			this.loadingSpriteFolders.add(folder);
			this.imagesService
				.getSpriteContent(indicationId)
				.pipe(
					tap(safeHtml => {
						this.accumulatedSpriteMarkup.set(folder, safeHtml);
						this.injectedSpritesContentSubject.next(new Map<string, SafeHtml>(this.accumulatedSpriteMarkup.entries()));
					}),
					takeUntil(this.serviceDestroy$),
					finalize(() => this.loadingSpriteFolders.delete(folder))
				)
				.subscribe();
		};

		for (const indicationId of uniqueIndicationIds) {
			loadSpriteIfNeeded(indicationId);
		}

		loadSpriteIfNeeded(null);
	}

	ngOnDestroy(): void {
		this.serviceDestroy$.next();
		this.serviceDestroy$.complete();
	}
}
